// Which finished music video a rewards post is submitted for. The server's
// rewards.submitLink takes either an exported Studio project id or a completed
// quick music video id (its owned-video lookup falls back to ai_music_videos),
// and rewards.eligibleVideos lists both kinds, newest first.

export type RewardEligibleVideo = {
  id: string;
  title: string;
  updatedAt?: Date | string | null;
};

export type RewardVideoOption = { id: string; label: string };

export type RewardVideoContext = {
  projectId: string | null;
  exportedVideoUrl?: string | null;
};

function shortDate(value: RewardEligibleVideo["updatedAt"]): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function rewardVideoOptions(
  videos: readonly RewardEligibleVideo[],
  context: RewardVideoContext,
): RewardVideoOption[] {
  // A Studio export also writes its library copy to ai_music_videos under the
  // project's own id, so the list can name one video twice; keep the first.
  const options: RewardVideoOption[] = [];
  for (const video of videos) {
    if (options.some((option) => option.id === video.id)) continue;
    const date = shortDate(video.updatedAt);
    options.push({
      id: video.id,
      label: date ? `${video.title} · ${date}` : video.title,
    });
  }
  // The open Studio project can be submitted as soon as it is exported, even
  // while the list is still loading or before it reflects a fresh export.
  if (
    context.projectId &&
    context.exportedVideoUrl &&
    !options.some((option) => option.id === context.projectId)
  ) {
    options.unshift({ id: context.projectId, label: "This music video" });
  }
  return options;
}

export function pickRewardVideoId(
  options: readonly RewardVideoOption[],
  context: RewardVideoContext,
  pickedId: string,
): string {
  const has = (id: string | null) =>
    Boolean(id) && options.some((option) => option.id === id);
  if (has(pickedId)) return pickedId;
  if (has(context.projectId)) return context.projectId as string;
  return options[0]?.id ?? "";
}
